import { makeAutoObservable, computed } from 'mobx';
import RootStore from 'stores/RootStore';
import {
  ProductionStatusLevel,
  OperationsSnapshotData,
  AlertItem,
  LoadsByStatus,
  FleetStatus,
  PlantStatus,
} from './OperationsSnapshotUtils';

class DashboardStore {
  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Get today's date string for filtering
  get todayDateString(): string {
    return new Date().toISOString().split('T')[0];
  }

  // Total yards batched today (mock data)
  get totalYardsBatchedToday(): number {
    return 342;
  }

  // Loads by status (mock data)
  get loadsByStatus(): LoadsByStatus {
    return {
      scheduled: 8,
      batching: 2,
      loading: 1,
      inTransit: 3,
      onSite: 2,
      discharging: 1,
      completed: 12,
    };
  }

  // Fleet status (mock data)
  get fleetStatus(): FleetStatus {
    return {
      available: 8,
      onDelivery: 4,
      maintenance: 1,
      outOfService: 1,
      total: 14,
    };
  }

  // Plant statuses (mock data)
  get plantStatuses(): PlantStatus[] {
    return [
      {
        plantId: 'plant-1',
        plantName: 'Main Plant',
        operationalStatus: 'operational',
        hourlyCapacityYards: 50,
      },
      {
        plantId: 'plant-2',
        plantName: 'South Plant',
        operationalStatus: 'operational',
        hourlyCapacityYards: 35,
      },
    ];
  }

  // Quality metrics for today (mock data)
  get todaysQualityMetrics() {
    return {
      slumpTestsPassed: 18,
      slumpTestsTotal: 20,
      rejectionsToday: 1,
    };
  }

  // Active alerts (mock data based on mock values)
  get activeAlerts(): AlertItem[] {
    const alerts: AlertItem[] = [];

    // 1 truck out of service
    alerts.push({
      id: 'trucks-oos',
      message: '1 truck out of service',
      severity: 'warning',
    });

    // 1 batch rejection
    alerts.push({
      id: 'rejections',
      message: '1 batch rejected today',
      severity: 'error',
    });

    return alerts;
  }

  // Overall status based on alerts
  get overallStatus(): { level: ProductionStatusLevel; message: string } {
    const errorCount = this.activeAlerts.filter((a) => a.severity === 'error').length;
    const warningCount = this.activeAlerts.filter((a) => a.severity === 'warning').length;

    if (errorCount > 0) {
      return {
        level: ProductionStatusLevel.CriticalIssues,
        message: `${errorCount} critical issue${errorCount > 1 ? 's' : ''} require attention`,
      };
    }
    if (warningCount > 0) {
      return {
        level: ProductionStatusLevel.MinorConcerns,
        message: `${warningCount} warning${warningCount > 1 ? 's' : ''} to review`,
      };
    }
    return {
      level: ProductionStatusLevel.OnTrack,
      message: 'All systems operational',
    };
  }

  // Combined snapshot data
  get operationsSnapshot(): OperationsSnapshotData {
    return {
      overallStatus: this.overallStatus,
      dailyProduction: {
        actualYards: this.totalYardsBatchedToday,
        targetYards: this.dailyTargetYards,
      },
      activeLoads: this.loadsByStatus,
      fleetStatus: this.fleetStatus,
      plantStatuses: this.plantStatuses,
      activeAlerts: this.activeAlerts,
      qualityMetrics: this.todaysQualityMetrics,
    };
  }

  // Calculate target yards based on plant capacity (8-hour shift)
  get dailyTargetYards(): number {
    const operationalPlants = this.plantStatuses.filter(
      (p) => p.operationalStatus === 'operational'
    );
    const totalCapacity = operationalPlants.reduce(
      (sum, p) => sum + p.hourlyCapacityYards,
      0
    );
    // Assume 8-hour workday at 75% efficiency
    return Math.round(totalCapacity * 8 * 0.75) || 500; // Default 500 if no plants
  }

  // Weekly data for charts (mock data)
  get weeklyYardsByDay(): number[] {
    // Realistic 7-day production data (Mon-Sun)
    return [285, 312, 298, 325, 310, 180, 342];
  }

  // Mix designs used today (mock data)
  get todaysMixDesignDistribution(): { mixCode: string; yards: number }[] {
    return [
      { mixCode: '4000PSI', yards: 120 },
      { mixCode: '3500PSI', yards: 95 },
      { mixCode: '5000PSI', yards: 68 },
      { mixCode: '3000PSI', yards: 42 },
      { mixCode: 'FlowFill', yards: 17 },
    ];
  }

  // Active drivers count (mock data)
  get activeDriversCount(): number {
    return 12;
  }
}

export default DashboardStore;
